// @flow

import { useMemo } from 'react';

const DEVICE_ID_KEY = 'pix_jsr_device_id';

const OS_PATTERNS = [
  { regex: /Windows NT ([\d.]+)/, name: 'Windows' },
  { regex: /Mac OS X ([\d_.]+)/, name: 'macOS' },
  { regex: /Android ([\d.]+)/, name: 'Android' },
  { regex: /iPhone OS ([\d_]+)/, name: 'iOS' },
  { regex: /Linux/, name: 'Linux' },
];

function getOrCreateDeviceId(): string {
  const stored = typeof localStorage !== 'undefined' && localStorage.getItem(DEVICE_ID_KEY);
  if (stored) {
    return stored;
  }
  const deviceId = window.crypto.randomUUID();
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
}

function parseOsVersion(): string {
  const ua = navigator.userAgent;

  for (const { regex, name } of OS_PATTERNS) {
    const match = ua.match(regex);
    if (match) {
      const version = match[1] ? match[1].replace(/_/g, '.') : '';
      return version ? `${name} ${version}` : name;
    }
  }

  return 'Unknown';
}

function formatTimezoneOffset(): string {
  const offsetMinutes = new Date().getTimezoneOffset();
  const sign = offsetMinutes <= 0 ? '+' : '-';
  const absMinutes = Math.abs(offsetMinutes);
  const hours = String(Math.floor(absMinutes / 60)).padStart(2, '0');
  const minutes = String(absMinutes % 60).padStart(2, '0');
  return `${sign}${hours}:${minutes}`;
}

export type RiskSignals = {|
  +device_id: string,
  +screenDimensions: {| +height: number, +width: number |},
  +language: string,
  +userTimeZoneOffset: string,
  +osVersion: string,
  +elapsedTimeSinceBoot: number,
  +isDevModeEnabled: boolean,
|};

export function collectRiskSignals(): RiskSignals {
  return {
    device_id: getOrCreateDeviceId(),
    screenDimensions: {
      height: window.screen.height,
      width: window.screen.width,
    },
    language: navigator.language.split('-')[0],
    userTimeZoneOffset: formatTimezoneOffset(),
    osVersion: parseOsVersion(),
    elapsedTimeSinceBoot: Math.round(performance.now()),
    isDevModeEnabled: false,
  };
}

export function useRiskSignals(): () => RiskSignals {
  return useMemo(() => collectRiskSignals, []);
}
